import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { firstValueFrom } from "rxjs";
import { Gutachten } from "../../../api/models/gutachten";
import { GutachtenListRetrieve } from "../../../api/models/gutachten-list-retrieve";
import { GutachtenService } from "../../../api/services/gutachten.service";
import { BerichtService } from "../../services/bericht.service";

@Component({
  selector: "app-gutachten-auswahl",
  templateUrl: "./gutachten-auswahl.component.html",
  styleUrls: ["./gutachten-auswahl.component.scss"],
  standalone: false,
})
export class GutachtenAuswahlComponent implements OnInit {
  @Input({ required: true }) aktId: number;
  @Input() setDisabledWhileLoading: (disabled: boolean) => void;

  @Output() onGutachtenChanged: EventEmitter<Gutachten> = new EventEmitter<Gutachten>();

  loadingOptions: boolean = false;
  gutachtenSelectControl = new FormControl();
  duplizierenControl = new FormControl(true);
  gutachtenListe: GutachtenListRetrieve = null;
  gutachtenListeOptions: { value: string; label: string }[];

  allowCreatingNewGutachten: boolean;

  constructor(
    private route: ActivatedRoute,
    private gutachtenService: GutachtenService,
    private oidcSecurityService: OidcSecurityService,
    private berichtService: BerichtService,
  ) {}

  ngOnInit() {
    this.gutachtenListe = this.route.snapshot.data.bericht;
    this.refreshGutachtenOptions(this.gutachtenListe);

    if (this.gutachtenListeOptions.length > 0) {
      this.gutachtenSelectControl.setValue(this.gutachtenListeOptions[0].value, { emitEvent: false });
      this.changeGutachten(this.gutachtenListeOptions[0].value);
    }
  }

  async changeGutachten(gutachtenId: string) {
    this.setDisabledWhileLoading(true);
    const gutachten: Gutachten = await this.berichtService.fetchGutachten(this.aktId, parseInt(gutachtenId));
    this.onGutachtenChanged.emit(gutachten);
    this.setDisabledWhileLoading(false);
  }

  async createGutachten(aktId: number) {
    if (!this.allowCreatingNewGutachten) {
      return;
    }
    this.loadingOptions = true;
    this.setDisabledWhileLoading(true);
    let gutachten: Gutachten;
    if (this.duplizierenControl.value && this.gutachtenListeOptions.length > 0) {
      const selectedGutachten =
        this.gutachtenListeOptions.length > 1 ? this.gutachtenSelectControl.value : this.gutachtenListeOptions[0].value;
      gutachten = await firstValueFrom(
        this.gutachtenService.gutachtenOdataCloneCreate({
          akt_id: aktId,
          token: this.oidcSecurityService.getToken(),
          Oid: parseInt(selectedGutachten),
        }),
      );
    } else {
      gutachten = await firstValueFrom(
        this.gutachtenService.gutachtenCreateCreate({
          akt_id: aktId,
          token: this.oidcSecurityService.getToken(),
        }),
      );
    }
    this.refreshGutachtenOptions(undefined, gutachten.GutachtenId.toString());
    this.loadingOptions = false;
    this.setDisabledWhileLoading(false);
    this.onGutachtenChanged.emit(gutachten);
  }

  refreshGutachtenOptions(preloadedGutachtenListe?: GutachtenListRetrieve, refreshSelectWithGutachtenId?: string) {
    this.allowCreatingNewGutachten = false;
    if (preloadedGutachtenListe) {
      this.allowCreatingNewGutachten = preloadedGutachtenListe.allow_create_gutachten;
      this.sortAndFormatGutachtenListe();
    } else {
      this.loadingOptions = true;
      this.gutachtenService
        .gutachtenListRetrieveCreate({
          token: this.oidcSecurityService.getToken(),
          akt_id: this.aktId,
        })
        .subscribe((response) => {
          this.loadingOptions = false;
          this.gutachtenListe = response;
          this.allowCreatingNewGutachten = this.gutachtenListe.allow_create_gutachten;
          this.sortAndFormatGutachtenListe();
          if (refreshSelectWithGutachtenId) {
            this.gutachtenSelectControl.setValue(refreshSelectWithGutachtenId, { emitEvent: false });
          }
        });
    }
  }

  sortAndFormatGutachtenListe() {
    this.gutachtenListe.Gutachten.sort(
      (a, b) => new Date(b.ErstellDatum).getTime() - new Date(a.ErstellDatum).getTime(),
    );
    this.gutachtenListeOptions = this.gutachtenListe.Gutachten.map((entry) => ({
      value: entry.Oid.toString(),
      label: `${new Date(entry.ErstellDatum).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })} ${new Date(entry.ErstellDatum).toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      })}`,
    }));
  }
}
